/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DateTime } from '@easepick/bundle';
import { DatetimePickerResult } from '@squiz/sds';
import { LogsQuery } from '../../Logs.constants';
import {
  PRESET_TIMES,
  PRESET_TIME_CUSTOM,
  PresetTimeType,
  PresetTimeUnit,
} from '../../elements/SelectPresetTime/SelectPresetTime.constants';
import { isValidDate } from '../IsValidDate/IsValidDate';

/**
 * Derive date range from the current search params.
 */
export const getDateRangeFromSearchParams = (searchParams: URLSearchParams): [DatetimePickerResult, boolean] => {
  const dateRange = searchParams.get(LogsQuery.date.range) as string;
  const dateTo = searchParams.get(LogsQuery.date.to) as string;
  const dateFrom = searchParams.get(LogsQuery.date.from) as string;

  const getDateTimeMinutesOffset = (time: number, minutes: number): number => {
    const minutesInMilliseconds = 1000 * 60 * minutes;

    return time - minutesInMilliseconds;
  };

  const getCustomRange = (): [DatetimePickerResult, boolean] => {
    // Logs are historical and kept for 14 days.
    // This means we restrict the date[from] param accordingly.
    // .format is used to round down to the beginning of the first day.
    const maxDateTime = new DateTime(new Date());
    const minDateTime = new DateTime(maxDateTime.clone().subtract(14, 'day').format('YYYY-MM-DD'));

    // Default the custom range to the previous 15 minutes
    const defaultRangeEnd = maxDateTime.getTime();
    const defaultRangeStart = getDateTimeMinutesOffset(defaultRangeEnd, 15);

    const defaultRange = {
      endDateTime: defaultRangeEnd,
      startDateTime: defaultRangeStart,
    };

    if (!dateTo && !dateFrom) {
      return [defaultRange, false];
    }

    if (!isValidDate(dateTo) || !isValidDate(dateFrom)) {
      return [defaultRange, true];
    }

    const endDateTime = new Date(dateTo).getTime();
    const startDateTime = new Date(dateFrom).getTime();

    const rangeIsReversed = startDateTime > endDateTime;
    const startIsTooEarly = startDateTime < minDateTime.getTime();
    const endIsTooLate = endDateTime > maxDateTime.getTime();

    if (rangeIsReversed || startIsTooEarly || endIsTooLate) {
      return [defaultRange, true];
    }

    return [
      {
        endDateTime,
        startDateTime,
      },
      false,
    ];
  };

  const getRangeStartTimeByUnit = (endDate: DateTime, presetTime: PresetTimeType): number => {
    const presetRangeEnd = endDate.getTime();
    const { amount, unit } = presetTime;

    // Handle minutes
    if (unit === PresetTimeUnit.minute) {
      return getDateTimeMinutesOffset(presetRangeEnd, amount);
    }

    // Handle hours
    if (unit === PresetTimeUnit.hour) {
      return getDateTimeMinutesOffset(presetRangeEnd, amount * 60);
    }

    // Handle other units
    const startDate = new DateTime(endDate.clone().subtract(amount, unit).format('YYYY-MM-DD HH:mm:ss'));

    return startDate.getTime();
  };

  const getPresetRange = (): [DatetimePickerResult, boolean] => {
    const defaultTimeOption = PRESET_TIMES[0];
    let presetTime = defaultTimeOption;

    // Update the range if it's available in the URL
    if (dateRange) {
      const presetTimeOption = PRESET_TIMES.find((time) => {
        const [amount, unit] = dateRange.split('+');
        return time.amount === Number(amount) && time.unit === unit;
      });

      presetTime = presetTimeOption ?? defaultTimeOption;
    }

    const endDate = new DateTime(new Date());
    const startDateTime = getRangeStartTimeByUnit(endDate, presetTime);

    return [
      {
        endDateTime: endDate.getTime(),
        startDateTime: startDateTime,
      },
      false,
    ];
  };

  if (dateRange === PRESET_TIME_CUSTOM) {
    return getCustomRange();
  }

  return getPresetRange();
};
